import { useEffect, useMemo, useState } from "react";

import { Action, useKBar, useRegisterActions } from "kbar";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useDebounce } from "use-debounce";

import { useNavigate } from "src/router";
import { useUser } from "src/contexts/user-context";
import { useSearchWorkspacesQuery } from "src/graphql";
import { useEntitlements } from "src/hooks/use-entitlement";
import {
  AudienceIcon,
  DestinationIcon,
  DescriptionIcon,
  ModelIcon,
  SettingsIcon,
  SourceIcon,
  SyncIcon,
  UserIcon,
} from "@hightouch/htui";
import { switchWorkspace } from "src/utils/workspaces";
import {
  type RecentWorkspace,
  useRecentWorkspaces,
} from "src/hooks/use-recent-workspaces";
import { useCommandBar } from "src/components/commandbar/context";

const isNumeric = (value: string) => {
  return /^-?\d+$/.test(value);
};

const SWITCH_WORKSPACE_ROOT_ID = "workspace.switch";

// Number of recent workspaces surfaced in the empty state of the
// "Switch workspace" command-bar menu.
const MAX_DISPLAYED_RECENT_WORKSPACES = 5;

type WorkspaceCommandDisplayInput = {
  id: string | number;
  name: string;
  slug: string | null;
  organizationName?: string | null;
  memberCount?: number | null;
  supportAccessType?: string | null;
};

/**
 * Shared formatting for "Switch workspace" command-bar entries so recents and
 * search results render identically: a `Name (id) with N members` title and a
 * `slug (Organization)` subtitle.
 */
export function getWorkspaceCommandDisplay(
  workspace: WorkspaceCommandDisplayInput,
  canImpersonate: boolean,
): { name: string; subtitle: string; cannotAccess: boolean } {
  const cannotAccess = canImpersonate && workspace.supportAccessType === "none";
  const memberSuffix =
    workspace.memberCount != null
      ? ` with ${workspace.memberCount} members`
      : "";
  const subtitle = cannotAccess
    ? "Support access disabled"
    : [
        workspace.slug,
        workspace.organizationName ? `(${workspace.organizationName})` : null,
      ]
        .filter(Boolean)
        .join(" ");
  return {
    name: `${workspace.name} (${workspace.id})${memberSuffix}`,
    subtitle,
    cannotAccess,
  };
}

/**
 * Build the command-bar actions for the recently switched-to workspaces,
 * most-recent first. Excludes the active workspace and caps the result at
 * {@link MAX_DISPLAYED_RECENT_WORKSPACES}. Metadata comes straight from the
 * persisted recents, so workspaces in other organizations (e.g. customer
 * workspaces a Hightouch admin switched into) still render.
 */
export function buildRecentWorkspaceActions({
  recentWorkspaces,
  currentWorkspaceId,
  onSwitch,
}: {
  recentWorkspaces: RecentWorkspace[];
  currentWorkspaceId: unknown;
  onSwitch: (workspace: RecentWorkspace) => void;
}): Action[] {
  return recentWorkspaces
    .filter((workspace) => String(workspace.id) !== String(currentWorkspaceId))
    .slice(0, MAX_DISPLAYED_RECENT_WORKSPACES)
    .map((workspace) => {
      const display = getWorkspaceCommandDisplay(workspace, false);
      return {
        id: `${SWITCH_WORKSPACE_ROOT_ID}.recent.${workspace.id}`,
        section: { name: "Recent", priority: 20 },
        parent: SWITCH_WORKSPACE_ROOT_ID,
        name: display.name,
        subtitle: display.subtitle || undefined,
        keywords: [
          workspace.id,
          workspace.name,
          workspace.slug ?? "",
          workspace.organizationName ?? "",
        ].join(" "),
        perform: () => onSwitch(workspace),
      };
    });
}

/**
 * Add dynamic Workspace options to the CommandBar
 */
function useSwitchWorkspaceActions() {
  const { user, workspace } = useUser();
  const { setIsFetching } = useCommandBar();
  const { recentWorkspaces, addRecentWorkspace } = useRecentWorkspaces();

  // Subscribe to search query.
  const { query, search, currentRootActionId } = useKBar((state) => ({
    search: state.searchQuery,
    currentRootActionId: state.currentRootActionId,
  }));

  // Register root action.
  useRegisterActions(
    user
      ? [
          {
            id: SWITCH_WORKSPACE_ROOT_ID,
            section: {
              name: "User",
              priority: 5,
            },
            name: "Switch workspace",
            keywords: "workspace",
            icon: UserIcon as any,
            shortcut: ["s", "w"],
            perform: () => {
              query.setCurrentRootAction(SWITCH_WORKSPACE_ROOT_ID);
              query.toggle();
            },
          },
        ]
      : [],
    [user, query],
  );

  // Surface recently switched-to workspaces in the empty state of the
  // "Switch workspace" menu. Once the user starts typing we hand off to the
  // search results below, so recents are not shown alongside a query.
  const recentWorkspaceActions = useMemo<Action[]>(() => {
    if (
      !user ||
      currentRootActionId !== SWITCH_WORKSPACE_ROOT_ID ||
      search.length > 0
    ) {
      return [];
    }
    return buildRecentWorkspaceActions({
      recentWorkspaces,
      currentWorkspaceId: workspace?.id,
      onSwitch: (recent) => {
        addRecentWorkspace(recent);
        void switchWorkspace(recent.id);
      },
    });
  }, [
    user,
    currentRootActionId,
    search,
    recentWorkspaces,
    workspace?.id,
    addRecentWorkspace,
  ]);

  useRegisterActions(recentWorkspaceActions, [recentWorkspaceActions]);

  const [_searchTerm, setSearchTerm] = useState("");

  const [searchTerm, { isPending: isSearchPending }] = useDebounce(
    _searchTerm,
    500,
  );

  useEffect(() => {
    if (currentRootActionId === "workspace.switch") {
      setSearchTerm(search);
    }
  }, [search, currentRootActionId]);

  const { data: workspaceData, isLoading } = useSearchWorkspacesQuery(
    {
      query: `%${searchTerm}%`,
      limit: 20,
      id: searchTerm,
      isIdSearch: isNumeric(searchTerm),
    },
    {
      meta: { isAdmin: user?.can_impersonate },
      enabled:
        Boolean(user) &&
        currentRootActionId === "workspace.switch" &&
        searchTerm.length > 0,
      select: (data) => [
        // prioritize searches by ID since they are more specific.
        ...(data.workspacesById || []),
        ...(data.workspaces || []),
      ],
    },
  );

  const isFetching = isLoading || isSearchPending();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => setIsFetching(isFetching), [isFetching]);

  // Set to empty list if we are still loading or debouncing,
  // otherwise the command bar will try to do some in-memory filtering,
  // which looks weird as the query is loading.
  const searchedWorkspaces = isFetching ? [] : (workspaceData ?? []);

  // Dynamically register workspace options.
  useRegisterActions(
    user
      ? searchedWorkspaces.map((w) => {
          const organizationName = w.organization?.name ?? null;
          const memberCount = w.all_memberships_aggregate.aggregate?.count ?? 0;
          // XXX: This doesn't address the case where users are directly in a workspace
          const display = getWorkspaceCommandDisplay(
            {
              id: w.id,
              name: w.name,
              slug: w.slug,
              organizationName,
              memberCount,
              supportAccessType: w.organization?.support_access_type ?? null,
            },
            Boolean(user.can_impersonate),
          );
          return {
            id: `${SWITCH_WORKSPACE_ROOT_ID}.${w.id}`,
            section: "Workspace",
            parent: SWITCH_WORKSPACE_ROOT_ID,
            name: display.name,
            subtitle: display.subtitle,
            keywords: [w.id, w.name, w.slug].join(" "),
            perform: () => {
              if (display.cannotAccess) {
                return;
              }
              addRecentWorkspace({
                id: String(w.id),
                name: w.name,
                slug: w.slug,
                organizationName,
                memberCount,
              });
              void switchWorkspace(w.id);
            },
          };
        })
      : [],
    [user, searchedWorkspaces, addRecentWorkspace],
  );
}

function useDocsActions() {
  useRegisterActions(
    [
      {
        id: "docs",
        section: "Docs",
        name: "Docs",
        keywords: "docs documentation",
        icon: DescriptionIcon as any,
        perform: () =>
          // biome-ignore lint/suspicious/noAssignInExpressions: intentional assignment in expression
          (window.location.href = import.meta.env.VITE_DOCS_URL as string),
      },
    ],
    [],
  );
}

export function useWorkspaceActions() {
  // Mount routes to CommandBar
  const actions: Action[] = [];

  const navigate = useNavigate();
  const { user, workspace } = useUser();
  const { audiencesEnabled } = useFlags();
  const { data: entitlementsData } = useEntitlements(false);
  const hasAudiencesEntitlement = entitlementsData.entitlements.audiences;

  useDocsActions();
  useSwitchWorkspaceActions();

  if (user) {
    if (workspace) {
      // Nav actions.
      actions.push(
        ...[
          /* Sync Actions */
          {
            id: "syncs",
            section: {
              name: "Workspace",
              priority: 10,
            },
            name: "Syncs",
            keywords: "syncs",
            icon: SyncIcon as any,
            perform: () => navigate("/syncs"),
          },
          {
            parent: "syncs",
            section: "Workspace",
            id: "syncs.new",
            name: "New sync",
            keywords: "syncs new add create",
            perform: () => navigate("/syncs/new"),
          },

          /* Model Actions */
          {
            id: "models",
            section: "Workspace",
            name: "Models",
            keywords: "models",
            icon: ModelIcon as any,
            perform: () => navigate("/models"),
          },
          {
            parent: "models",
            section: "Workspace",
            id: "models.new",
            name: "New model",
            keywords: "models new add create",
            perform: () => navigate("/models/new"),
          },

          ...(audiencesEnabled || hasAudiencesEntitlement
            ? [
                {
                  id: "audiences",
                  section: "Workspace",
                  name: "Audiences",
                  keywords: "audiences",
                  icon: AudienceIcon as any,
                  perform: () => navigate("/audiences"),
                },
                {
                  id: "audiences.new",
                  parent: "audiences",
                  section: "Workspace",
                  name: "New audience",
                  keywords: "audiences new add create",
                  perform: () => navigate("/audiences/new"),
                },
              ]
            : []),

          /* Source Actions */
          {
            id: "sources",
            section: "Workspace",
            name: "Sources",
            keywords: "sources",
            icon: SourceIcon as any,
            perform: () => navigate("/sources"),
          },
          {
            parent: "sources",
            section: "Workspace",
            id: "sources.new",
            name: "New source",
            keywords: "sources new add create",
            perform: () => navigate("/sources/new"),
          },

          /* Destination Actions */
          {
            id: "destinations",
            section: "Workspace",
            name: "Destinations",
            keywords: "destinations",
            icon: DestinationIcon as any,
            perform: () => navigate("/destinations"),
          },
          {
            section: "Workspace",
            parent: "destinations",
            id: "destinations.new",
            name: "New destination",
            keywords: "destinations new add create",
            perform: () => navigate("/destinations/new"),
          },

          /* Settings */
          {
            id: "settings",
            section: "Workspace",
            name: "Settings",
            keywords: "settings",
            icon: SettingsIcon as any,
            perform: () => navigate("/settings"),
          },
          ...[
            { id: "workspace", name: "Workspace", page: "/settings/workspace" },
            { id: "members", name: "Members", page: "/settings/members" },
            { id: "integrations", name: "Integrations", page: "/extensions" },
            { id: "alerts", name: "Alerts", page: "/alerting" },
            { id: "api-keys", name: "API Keys", page: "/settings/api-keys" },
            { id: "storage", name: "Storage", page: "/settings/storage" },
            { id: "tunnels", name: "Tunnels", page: "/settings/tunnels" },
            { id: "git-sync", name: "Git sync", page: "/extensions/git-sync" },
          ].map(({ id, name, page }) => ({
            parent: "settings",
            id: `settings.${id}`,
            section: "Workspace",
            name: name,
            keywords: `settings ${id}`,
            perform: () => navigate(page),
          })),
        ].filter((a) => !!a),
      );
    }
  }
  useRegisterActions(actions, [
    user,
    workspace,
    actions,
    audiencesEnabled,
    hasAudiencesEntitlement,
  ]);
}
